import { useEffect, useState } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import {
  createLessonsAPIEndpoint,
  EndPoints as LessonEndPoints,
} from "../../../services/LessonAPI";
import { enqueueSnackbar } from "notistack";
import { Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import {
  createStudentsAPIEndpoint,
  EndPoints as StudentEndPoints,
} from "../../../services/StudentAPIs";
import { CheckCircleOutlineOutlined, CheckOutlined, InfoOutlined, ListAltOutlined, PersonOutlineOutlined, QrCodeScannerOutlined } from "@mui/icons-material";

function Attendance() {
  const [scanResult, setScanResult] = useState(null);
  const [lessonId, setLessonId] = useState(""); // selected lesson
  const [message, setMessage] = useState("");
  const [lessons, setLessons] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  // Fetch lessons from backend
  const fetchLessons = () => {
    createLessonsAPIEndpoint(LessonEndPoints.getLessons)
      .fetch()
      .then((res) => setLessons(res.data))
      .catch((err) => {
        console.error("Failed to fetch lessons:", err);
        enqueueSnackbar("Failed to fetch lessons.", { variant: "error" });
      });
  };
  // Start QR scanner when component mounts
  useEffect(() => {
    fetchLessons();
    const scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: 250 });

    scanner.render(
      (decodedText) => {
        console.log("QR Code scanned:", decodedText);
        setScanResult(decodedText);
      },
      (error) => {
        console.warn("QR Scan error:", error);
      }
    );

    return () => {
      scanner
        .clear()
        .catch((err) => console.error("Failed to clear scanner:", err));
    };
  }, []);

  // Call backend to mark attendance
  const handleSaveAttendance = async () => {
    if (!scanResult || !lessonId) {
      setMessage("⚠️ Please select a lesson and scan a student QR code first.");
      return;
    }
    setLoading(true);
    createStudentsAPIEndpoint(StudentEndPoints.attendance)
      .postWithToken({ lessonId, qrCode: scanResult })
      .then((res) => {
        enqueueSnackbar("Attendance saved successfully!", { variant: "success" });
        setAttendance((prev) => [...prev,  {
          studentName : res.data.student,
          lessonTitle : res.data.lesson,
          status : "Present",
          time : new Date().toLocaleTimeString(),
        }]);
        console.log("Attendance saved:", res.data);
        setScanResult(null);// reset after save
        setMessage("");
      })
      .catch((err) => {
        enqueueSnackbar("Failed to save attendance.", { variant: "error" });
        console.error("Failed to save attendance:", err);
        setMessage("❌  " + err.response?.data?.message || err.message);
      }).finally(() => setLoading(false));
  };
  const handleResumeScanner = () => {
    const scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: 250 });
    scanner.render((decodedText) => {
      setScanResult(decodedText);
    });
    setScanResult(null);
    setMessage("");
  };
return (
  <Box className="attendance-page dashboard-section">
    {/* Page Header */}
    <Box className="attendance-header">
      <Box>
        <Typography className="attendance-title">Lesson Attendance</Typography>

        <Typography className="attendance-subtitle">
          Select a lesson and scan students' QR codes to record attendance.
        </Typography>
      </Box>
    </Box>

    {/* Lesson Selection */}
    <Paper className="attendance-card lesson-card">
      <Box className="card-heading">
        <Box className="heading-icon">
          <QrCodeScannerOutlined />
        </Box>

        <Box>
          <Typography className="card-title">Select Lesson</Typography>

          <Typography className="card-description">
            Choose the lesson you want to record attendance for.
          </Typography>
        </Box>
      </Box>

      <TextField
        select
        label="Lesson"
        value={lessonId}
        onChange={(e) => setLessonId(e.target.value)}
        fullWidth
        className="lesson-select"
      >
        {lessons.map((lesson) => (
          <MenuItem key={lesson.id} value={lesson.id}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: 2,
              }}
            >
              <span>{lesson.title}</span>

              <Typography
                component="span"
                sx={{
                  fontSize: "13px",
                  color: "var(--secondaryColor)",
                  fontWeight: "bold",
                }}
              >
                {lesson.educationStageAr}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>
    </Paper>

    {/* Scanner */}
    <Paper className="attendance-card scanner-card">
      <Box className="card-heading">
        <Box className="heading-icon scanner-icon">
          <QrCodeScannerOutlined />
        </Box>

        <Box>
          <Typography className="card-title">Scan Student QR Code</Typography>

          <Typography className="card-description">
            Point the camera at the student's QR code.
          </Typography>
        </Box>
      </Box>

      {!lessonId && (
        <Box className="scanner-warning">
          <InfoOutlined />
          <Typography>Please select a lesson before scanning.</Typography>
        </Box>
      )}

      <Box className={`scanner-wrapper ${!lessonId ? "scanner-disabled" : ""}`}>
        <div id="reader" className="scanner" />
      </Box>
    </Paper>

    {/* Scan Result */}
    {scanResult && (
      <Paper className="attendance-card scan-result-card">
        <Box className="scan-result-header">
          <CheckCircleOutlineOutlined className="success-icon" />

          <Box>
            <Typography className="card-title">
              Student Scanned Successfully
            </Typography>

            <Typography className="scan-code">{scanResult}</Typography>
          </Box>
        </Box>

        <Box className="scan-actions">
          <Button
            variant="contained"
            className="save-attendance-btn"
            onClick={handleSaveAttendance}
            disabled={loading || !lessonId}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <>
                <CheckOutlined />
                Save Attendance
              </>
            )}
          </Button>

          <Button
            variant="outlined"
            className="scan-again-btn"
            onClick={handleResumeScanner}
            disabled={loading}
          >
            <QrCodeScannerOutlined />
            Scan Again
          </Button>
        </Box>
      </Paper>
    )}

    {/* Status */}
    {message && (
      <Paper className="attendance-message">
        <InfoOutlined />
        <Typography>{message}</Typography>
      </Paper>
    )}

    {/* Attendance Records */}
    <Paper className="attendance-card records-card">
      <Box className="records-header">
        <Box>
          <Typography className="card-title">Attendance Records</Typography>

          <Typography className="card-description">
            Students who have been marked present.
          </Typography>
        </Box>

        <Box className="records-count">{attendance.length}</Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Lesson</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Time</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {attendance.length > 0 ? (
              attendance.map((a, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Box className="student-cell">
                      <Box className="student-avatar">
                        <PersonOutlineOutlined />
                      </Box>

                      <Typography>{a.studentName || a.studentId}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>{a.lessonTitle || a.lessonId}</TableCell>

                  <TableCell>
                    <span
                      className={`attendance-status ${
                        a.status?.toLowerCase() === "present" ? "present" : ""
                      }`}
                    >
                      {a.status}
                    </span>
                  </TableCell>

                  <TableCell>
                    {a.time
                      ? new Date(a.time).toLocaleTimeString()
                      : new Date().toLocaleTimeString()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box className="empty-attendance">
                    <QrCodeScannerOutlined />

                    <Typography>No attendance records yet.</Typography>

                    <Typography>
                      Scan a student's QR code to get started.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Box>
);
}

export default Attendance;
